import { clsx } from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { getClothingCategoryBasicApi } from "../../apis/clothing-category.api";
import { ClothingCategoryBasicListDto } from "../../dto/clothing/clothing-category-basic-list.dto";
import { useRequest } from "../../lib/request/use-request";
import * as styles from "./index.module.scss";

const ALL_CATEGORY_ID = "all";
const ALL_CATEGORY_LABEL = "全部";

interface CategoryDropdownProps {
  activeId?: string;
  onSelect?: (id: string) => void;
}

export function CategoryDropdown({
  activeId,
  onSelect,
}: CategoryDropdownProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const { data, requestHandler: fetchCategories } =
    useRequest<ClothingCategoryBasicListDto>({
      api: getClothingCategoryBasicApi,
      title: "获取服装分类",
    });

  useEffect(() => {
    void fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (id: string) => {
      setIsOpen(false);
      if (onSelect) {
        onSelect(id);
        return;
      }
      void navigate(id === ALL_CATEGORY_ID ? "/" : `/?category=${id}`);
    },
    [navigate, onSelect],
  );

  const normalizedActiveId = activeId ?? ALL_CATEGORY_ID;

  const activeLabel = useMemo(() => {
    if (normalizedActiveId === ALL_CATEGORY_ID) return ALL_CATEGORY_LABEL;
    const match = data?.categoryList.find((c) => c.id === normalizedActiveId);
    return match?.name ?? ALL_CATEGORY_LABEL;
  }, [normalizedActiveId, data]);

  return (
    <div className={styles.categoryDropdown} ref={containerRef}>
      <button
        type="button"
        className={styles.categoryDropdownTrigger}
        onClick={() => setIsOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.categoryDropdownTriggerLabel}>
          {activeLabel}
        </span>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          aria-hidden="true"
          focusable="false"
          className={clsx(
            styles.categoryDropdownArrow,
            isOpen && styles.categoryDropdownArrowOpen,
          )}
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <ul className={styles.categoryDropdownMenu} role="listbox">
          <li>
            <button
              type="button"
              role="option"
              aria-selected={normalizedActiveId === ALL_CATEGORY_ID}
              className={clsx(styles.categoryDropdownItem, {
                [styles.categoryDropdownItemActive]:
                  normalizedActiveId === ALL_CATEGORY_ID,
              })}
              onClick={() => handleSelect(ALL_CATEGORY_ID)}
            >
              {ALL_CATEGORY_LABEL}
            </button>
          </li>
          {data?.categoryList.map((category) => {
            const isActive = category.id === normalizedActiveId;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={clsx(styles.categoryDropdownItem, {
                    [styles.categoryDropdownItemActive]: isActive,
                  })}
                  onClick={() => handleSelect(category.id)}
                >
                  {category.name}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
